import type { ComboGroupLayout } from '~/utils/types/ComboLayout'

/** Слой составной картинки комбо: картинка позиции на месте своего слота. */
export interface ComboSceneLayer {
  key: string
  img: string
  layout: ComboGroupLayout
}

/** Картинка позиции комбо приходит и строкой, и массивом объектов — зависит от эндпоинта. */
type ComboImage = string | null | undefined | Array<{ url?: string | null } | string>

export interface ComboSceneProduct {
  id: number | string
  img?: ComboImage
  is_default?: boolean
}

export interface ComboSceneGroup {
  id: number | string
  layout?: ComboGroupLayout | null
  products?: ComboSceneProduct[]
}

export const comboProductImage = (product: ComboSceneProduct | null | undefined): string | null => {
  const img = product?.img
  if (!img) return null
  if (typeof img === 'string') return img || null

  const first = img[0]
  if (!first) return null
  return (typeof first === 'string' ? first : first.url) || null
}

/**
 * Слои сцены из групп комбо и выбранных в них позиций.
 *
 * Слот без раскладки не рисуется: пока админ его не расставил, место у него неизвестно,
 * и подставлять «куда-нибудь» хуже, чем не показывать — картинка развалится. Слот без
 * картинки пропускаем по той же причине.
 *
 * Порядок слоёв задаёт `z`, а не порядок групп: админ расставляет глубину сам, и соус
 * на переднем плане не должен зависеть от того, какой группой он идёт в списке.
 *
 * @param selectedProductId вернуть выбранную позицию слота; по умолчанию — позиция
 * «по умолчанию» группы, а если её нет — первая. Так плитка меню показывает базовый набор,
 * а конструктор — то, что гость собрал прямо сейчас.
 */
export function comboSceneLayers(
  groups: ComboSceneGroup[] | null | undefined,
  selectedProductId?: (group: ComboSceneGroup) => number | string | null | undefined,
): ComboSceneLayer[] {
  if (!groups?.length) return []

  const layers: ComboSceneLayer[] = []

  for (const group of groups) {
    if (!group.layout) continue

    const products = group.products ?? []
    const selectedId = selectedProductId?.(group)
    const product
      = (selectedId != null && products.find(p => String(p.id) === String(selectedId)))
        || products.find(p => p.is_default)
        || products[0]

    const img = comboProductImage(product)
    if (!img) continue

    layers.push({ key: `${group.id}-${product?.id ?? 'empty'}`, img, layout: group.layout })
  }

  return layers.sort((a, b) => a.layout.z - b.layout.z)
}

/**
 * Слот комбо в УЖЕ ОФОРМЛЕННОМ заказе: бэкенд отдаёт его в `comboGroups` позиции.
 * Форма своя (`groupId` вместо `id`, товары уже только выбранные), поэтому приводим
 * её к сцене отдельно, а не тащим тип заказа в сущность комбо.
 */
export interface OrderComboSceneGroup {
  groupId: number | string
  layout?: ComboGroupLayout | null
  products?: ComboSceneProduct[]
}

/**
 * Слои составной картинки для позиции истории заказов.
 *
 * Картинку собираем из позиций, которые гость КУПИЛ, а не из состава комбо по умолчанию:
 * состав с тех пор могли поменять, а чек — нет. Раскладки нет (старый заказ, слот не
 * расставлен) — вернётся пусто, и витрина покажет обычную картинку комбо.
 */
export function orderComboSceneLayers(
  groups: OrderComboSceneGroup[] | null | undefined,
): ComboSceneLayer[] {
  if (!groups?.length) return []

  return comboSceneLayers(
    groups.map(group => ({ id: group.groupId, layout: group.layout, products: group.products })),
  )
}
